import { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import useSetTimeout from "app/pages/sales-intelligence/hooks/useSetTimeout";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";

import isDraftSignal from "../models/core/type-guards/isDraftSignal";
import { removeExpiredDraftSignalsAction } from "../store/action-creators";
import { selectAppliedSignalsIds, selectSignalsById } from "../store/selectors";

// Only schedule timeouts for signals expiring within 1 hour
// For signals expiring later, rely on refetches
const MAX_TIMEOUT_THRESHOLD_MS = 60 * 60 * 1000;

/**
 * Hook that automatically handles expired draft signals
 *
 * - Only schedules timeouts for signals expiring within 1 hour
 * - For signals expiring later, relies on refetches
 * - Automatically handles signals that expire while user is active
 */
export default function useSignalExpirationHandler(): void {
    const dispatch = useDispatch();

    const signalsById = useSelector(selectSignalsById);
    const appliedSignalsIds = useSelector(selectAppliedSignalsIds);

    const timeoutsRef = useRef<Map<string, number>>(new Map());

    const localSetTimeout = useSetTimeout();

    useEffect(() => {
        const currentTimeouts = timeoutsRef.current;

        if (!signalsById) {
            return;
        }

        const checkAndRemoveExpiredSignals = (): void => {
            if (!signalsById) {
                return;
            }

            const signals = Object.values(signalsById);
            const expiredIds = signalsService.getExpiredSignalIds(signals);

            if (expiredIds.length > 0) {
                dispatch(removeExpiredDraftSignalsAction({ expiredSignalIds: expiredIds }));
            }
        };

        currentTimeouts.forEach((timeout) => clearTimeout(timeout));
        currentTimeouts.clear();

        const now = new Date();

        Object.values(signalsById).forEach((signal) => {
            if (!isDraftSignal(signal)) {
                return;
            }

            const expirationTime = new Date(signal.expiresAt).getTime();
            const currentTime = now.getTime();
            const timeUntilExpiration = expirationTime - currentTime;

            // If already expired, will be caught by immediate check below
            if (timeUntilExpiration <= 0 || timeUntilExpiration > MAX_TIMEOUT_THRESHOLD_MS) {
                return;
            }

            const timeout = localSetTimeout(
                () => dispatch(removeExpiredDraftSignalsAction({ expiredSignalIds: [signal.id] })),
                timeUntilExpiration,
            );

            timeoutsRef.current.set(signal.id, timeout);
        });

        checkAndRemoveExpiredSignals();

        return () => {
            currentTimeouts.forEach((timeout) => clearTimeout(timeout));
            currentTimeouts.clear();
        };
    }, [signalsById, appliedSignalsIds]);
}
